import React, { useState, useRef, useEffect } from 'react';
import './CSR.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Static Data (Opportunities remain static) ---

const CSR_OPPORTUNITIES = [
  {
    id: 'infrastructure',
    icon: 'fa-building',
    title: 'Infrastructure Development',
    description: 'Support the expansion of our school with new classrooms, staff quarters, and elevators.',
    details: [
      'Each classroom: ₹6 lacs',
      'Staff quarters: ₹3 lacs',
      'Elevator installation: ₹15 lacs',
      'Name of contributing partner acknowledged with a plaque'
    ],
    color: '#E3A857'
  },
  {
    id: 'food',
    icon: 'fa-utensils',
    title: 'Food & Nutrition',
    description: 'Provide healthy, hearty meals for our students through meal sponsorship.',
    details: [
      'Daily vegetarian meal cost: ₹29,000',
      'Daily non-vegetarian meal cost: ₹33,000',
      'Includes breakfast, lunch, and dinner',
      'Ensures proper nutrition for growing children'
    ],
    color: '#C75C4D'
  },
  {
    id: 'child-sponsorship',
    icon: 'fa-graduation-cap',
    title: 'Child Sponsorship',
    description: 'Sponsor a child\'s complete education and empower them with knowledge.',
    details: [
      '₹15,000 annually per child',
      'Covers free quality education',
      'Includes all academic materials',
      'Transforms a child\'s future'
    ],
    color: '#2A3557'
  },
  {
    id: 'basic-needs',
    icon: 'fa-box',
    title: 'Basic Needs & Equipment',
    description: 'Support essential resources like Braille papers, stationery, and technical equipment.',
    details: [
      'Braille paper and stationery supplies',
      'Grocery and daily necessities',
      'Technical and assistive equipment',
      'Classroom and hostel essentials'
    ],
    color: '#E3A857'
  },
  {
    id: 'program-sponsorship',
    icon: 'fa-trophy',
    title: 'Program Sponsorship',
    description: 'Sponsor events and activities that enrich students\' lives and showcase their talents.',
    details: [
      'Chess Tournaments',
      'Cultural and Sports Events',
      'Medical and Eye Camps',
      'Special celebrations and programs'
    ],
    color: '#C75C4D'
  }
];

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main CSR Component ---

export default function CSR() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [csrPartners, setCsrPartners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  // Check for logged in user
  useEffect(() => {
    const checkUser = () => {
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          const id = userData.id || userData.user_id || null;
          if (id) {
            setUserId(id);
          }
        }
      } catch (e) {
        console.error('Error checking user:', e);
      }
    };
    checkUser();
    fetchCsrPartners();
    fetchCsrTestimonials();
  }, []);

  // Fetch CSR partners from database
  const fetchCsrPartners = async () => {
    try {
      setLoadingPartners(true);
      const result = await apiService.fetchCsrPartners();
      console.log('📋 CSR Partners response:', result);
      
      if (result.status === 'success' && result.data) {
        // Filter only active partners
        const activePartners = result.data.filter(p => p.status === 'active');
        setCsrPartners(activePartners);
      }
    } catch (error) {
      console.error('Error fetching CSR partners:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  // Fetch CSR testimonials from database
  const fetchCsrTestimonials = async () => {
    try {
      setLoadingTestimonials(true);
      const result = await apiService.fetchCsrTestimonials();
      console.log('📋 CSR Testimonials response:', result);
      
      if (result.status === 'success' && result.data) {
        // Filter only active testimonials
        const activeTestimonials = result.data.filter(t => t.status === 'active');
        setTestimonials(activeTestimonials);
      }
    } catch (error) {
      console.error('Error fetching CSR testimonials:', error);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare data for csr_registrations table
      const csrData = {
        user_id: userId || null,
        company_name: formData.companyName.trim(),
        contact_name: formData.contactName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        interest: formData.interest || null,
        message: formData.message.trim() || null,
        status: 'pending'
      };

      console.log('📤 Sending CSR registration:', csrData);

      // Save to csr_registrations table
      const result = await apiService.saveData('csr_registrations', csrData);
      
      console.log('📝 Registration result:', result);

      if (result.success) {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your interest! Our CSR team will contact you within 48 hours.'
        });
        setFormData({ companyName: '', contactName: '', email: '', phone: '', interest: '', message: '' });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: result.message || 'Failed to submit registration. Please try again.'
        });
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Network error: ' + (error.message || 'Please check your connection and try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clean HTML from Summernote
  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Get logo URL from JSON field
  const getLogoUrl = (logoData) => {
    if (!logoData) return null;
    try {
      const parsed = typeof logoData === 'string' ? JSON.parse(logoData) : logoData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return logoData;
    }
  };

  // Get image URL from JSON field
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return imageData;
    }
  };

  // Check if there are any active partners
  const hasPartners = csrPartners.length > 0;

  // Check if there are any active testimonials
  const hasTestimonials = testimonials.length > 0;

  return (
    <>
      <Header />
      <div className="csr-page devnar">
        <a className="skip-link" href="#main-csr">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-csr">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-csr">
            <Reveal>
              <span className="hero-badge">Corporate Social Responsibility</span>
              <h1>CSR Opportunities</h1>
              <p className="hero-subtitle">
                Partner with Devnar Foundation as part of your CSR initiatives and help transform 
                lives of visually challenged children through education, care, and empowerment.
              </p>
              <div className="hero-cta">
                <a href="#csr-form" className="btn btn-primary">
                  <i className="fas fa-handshake"></i> Become a Partner
                </a>
                <a href="#opportunities" className="btn btn-outline btn-outline-light">
                  <i className="fas fa-search"></i> Explore Opportunities
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <main id="main-csr" className="wrap page-content-csr">

          {/* --- Introduction (Static) --- */}
          <section className="csr-intro">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Make a Difference</span>
              <h2>Why Partner with Devnar?</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Devnar Foundation today is what it is only because of the unstinted support and love of well-wishers. 
                Reaching out can transform lives, can create magic!
              </p>
            </Reveal>
            <div className="intro-grid">
              <Reveal className="intro-card" delay={0}>
                <div className="intro-number">36+</div>
                <div className="intro-label">Years of Service</div>
                <p>Trusted institution with decades of proven impact</p>
              </Reveal>
              <Reveal className="intro-card" delay={80}>
                <div className="intro-number">500+</div>
                <div className="intro-label">Students Supported</div>
                <p>Lives transformed through education and care</p>
              </Reveal>
              <Reveal className="intro-card" delay={160}>
                <div className="intro-number">80G</div>
                <div className="intro-label">Tax Exemption</div>
                <p>All donations are tax exempted under section 80G</p>
              </Reveal>
              <Reveal className="intro-card" delay={240}>
                <div className="intro-number">100%</div>
                <div className="intro-label">Transparent Impact</div>
                <p>Complete visibility into how your contribution helps</p>
              </Reveal>
            </div>
          </section>

          {/* --- Opportunities (Static) --- */}
          <section id="opportunities" className="csr-opportunities">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">How You Can Help</span>
              <h2>CSR Opportunities</h2>
              <div className="rule" />
            </Reveal>
            <div className="opportunities-grid">
              {CSR_OPPORTUNITIES.map((opp, index) => (
                <Reveal key={opp.id} className="opportunity-card" delay={index * 80}>
                  <div className="opp-icon" style={{ background: `${opp.color}15`, color: opp.color }}>
                    <i className={`fas ${opp.icon}`}></i>
                  </div>
                  <h3>{opp.title}</h3>
                  <p className="opp-description">{opp.description}</p>
                  <ul className="opp-details">
                    {opp.details.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                  <div className="opp-line" style={{ background: opp.color }}></div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Current Partners (Dynamic from Database - HIDDEN if no data) --- */}
          {hasPartners && (
            <section className="csr-partners">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Our Network</span>
                <h2>Our CSR Partners</h2>
                <div className="rule" />
              </Reveal>

              {loadingPartners ? (
                <div className="loading-spinner-container">
                  <div className="spinner"></div>
                  <p>Loading partners...</p>
                </div>
              ) : (
                <>
                  <Reveal className="partners-list" delay={80}>
                    <div className="partner-tags">
                      {csrPartners.map((partner, index) => (
                        <span key={index} className="partner-tag">
                          {partner.company_name}
                        </span>
                      ))}
                    </div>
                  </Reveal>
                  <div className="partner-logos">
                    {csrPartners.map((partner, index) => {
                      const logoUrl = getLogoUrl(partner.logo);
                      return (
                        <Reveal key={partner.id} className="partner-logo-item" delay={index * 30}>
                          {logoUrl ? (
                            <img src={logoUrl} alt={partner.company_name} loading="lazy" />
                          ) : (
                            <div className="partner-logo-placeholder">
                              <i className="fas fa-building"></i>
                              <span>{partner.company_name}</span>
                            </div>
                          )}
                        </Reveal>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          )}

          {/* --- Testimonials (Dynamic from Database - HIDDEN if no data) --- */}
          {hasTestimonials && (
            <section className="csr-testimonials">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Testimonials</span>
                <h2>What Our Partners Say</h2>
                <div className="rule" />
              </Reveal>

              {loadingTestimonials ? (
                <div className="loading-spinner-container">
                  <div className="spinner"></div>
                  <p>Loading testimonials...</p>
                </div>
              ) : (
                <div className="testimonials-grid">
                  {testimonials.map((testimonial, index) => {
                    const imageUrl = getImageUrl(testimonial.image);
                    return (
                      <Reveal key={testimonial.id || index} className="testimonial-card" delay={index * 80}>
                        <div className="testimonial-header">
                          {imageUrl ? (
                            <img src={imageUrl} alt={testimonial.name} loading="lazy" />
                          ) : (
                            <div className="testimonial-avatar-placeholder">
                              <i className="fas fa-user-circle"></i>
                            </div>
                          )}
                          <div>
                            <h4>{testimonial.name}</h4>
                            {testimonial.designation && (
                              <span className="testimonial-role">{cleanHtml(testimonial.designation)}</span>
                            )}
                            {testimonial.company && (
                              <span className="testimonial-company">{cleanHtml(testimonial.company)}</span>
                            )}
                          </div>
                        </div>
                        <p className="testimonial-text">"{cleanHtml(testimonial.testimonial)}"</p>
                        <div className="testimonial-quote">
                          <i className="fas fa-quote-right"></i>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* --- Registration Form --- */}
          <section id="csr-form" className="csr-form-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Get Started</span>
              <h2>Fill Up the Registration Form Below</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Becoming a CSR partner of Devnar is an excellent opportunity to be a part of an equitable social change. 
                Contact our team to further discuss CSR possibilities.
              </p>
            </Reveal>

            <div className="form-wrapper">
              {formStatus.submitted && formStatus.success ? (
                <div className="form-success">
                  <i className="fas fa-check-circle success-icon"></i>
                  <h3>Registration Submitted!</h3>
                  <p>{formStatus.message}</p>
                  <p className="success-note">Our CSR team will reach out to you shortly.</p>
                </div>
              ) : formStatus.submitted && !formStatus.success ? (
                <div className="form-error">
                  <i className="fas fa-exclamation-circle error-icon"></i>
                  <h3>Submission Failed</h3>
                  <p>{formStatus.message}</p>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setFormStatus({ submitted: false, success: false, message: '' });
                      setIsSubmitting(false);
                    }}
                  >
                    <i className="fas fa-redo"></i> Try Again
                  </button>
                </div>
              ) : (
                <form className="csr-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="companyName">Company/Organization Name *</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        placeholder="Your company name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="contactName">Contact Person *</label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        required
                        placeholder="Full name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="interest">Area of Interest *</label>
                    <select
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select an area of interest...</option>
                      <option value="infrastructure">Infrastructure Development</option>
                      <option value="food">Food & Nutrition</option>
                      <option value="child-sponsorship">Child Sponsorship</option>
                      <option value="basic-needs">Basic Needs & Equipment</option>
                      <option value="program-sponsorship">Program Sponsorship</option>
                      <option value="general">General CSR Partnership</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message / Requirements</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your CSR goals, budget range, or any specific requirements..."
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span> Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i> Submit Registration
                        </>
                      )}
                    </button>
                    <p className="form-note">
                      <i className="fas fa-gift"></i> All donations to Devnar are tax exempted under section 80G of the IT Act.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </section>

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-csr">
            <div className="cta-content">
              <span className="cta-eyebrow">Join Our Mission</span>
              <h2>Be a Proud Partner in This Noble Effort</h2>
              <p>
                Help us add colors to the darkness of visually challenged children by making them 
                capable of painting their own rainbows, their own way!
              </p>
              <div className="cta-buttons">
                <a href="mailto:saibaba_goud@hotmail.com" className="btn btn-primary">
                  <i className="fas fa-envelope"></i> Email Our CSR Team
                </a>
                <a href="tel:+914027767931" className="btn btn-outline btn-outline-light">
                  <i className="fas fa-phone"></i> Call Us
                </a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}